import { useState, useEffect, useCallback } from 'react';
import {
  getAuthToken,
  fetchMovieBanner, fetchMovieList, fetchProgramDetail,
  fetchEpisodeList, fetchSearchList, getWatchList,
  addToWatchlist, removeFromWatchlist, fetchSubscriptionPlans,
  fetchGeneralMessage, completionEvent, fetchResumePosition,
} from '../api/client';

// ── Module-level home data cache ──────────────────────────────────────────────
let _homeCache = { banners: [], categories: [], loaded: false };

// ─────────────────────────────────────────────────────────────────────────────
// NO waitForToken needed anymore.
//
// Guest users now use /tv endpoints (API key only — no Firebase token needed).
// Logged-in users use /mobile endpoints (Firebase token attached automatically).
// client.js fetchMovieList() and fetchMovieBanner() choose the right endpoint
// based on whether _authToken is set. This means API calls can fire immediately
// on page load without waiting for Firebase initialisation.
// ─────────────────────────────────────────────────────────────────────────────

export function useLatestMovies(excludeId) {
  const [movies, setMovies] = useState([]);
  useEffect(() => {
    async function load() {
      if (_homeCache.loaded && _homeCache.categories.length) { extract(_homeCache.categories); return; }
      try {
        const res = await fetchMovieList();
        const cats = res?.data ?? [];
        if (!_homeCache.loaded) _homeCache = { ..._homeCache, categories: cats, loaded: true };
        extract(cats);
      } catch (e) { console.warn('useLatestMovies fetch failed', e); }
    }
    function extract(cats) {
      const latestCat = cats.find(r => /latest|new release|newest/i.test(r.genre || r.title || ''))
        || cats.find(r => /trend/i.test(r.genre || r.title || '')) || cats[0];
      if (!latestCat) return;
      const all = latestCat.movies ?? latestCat.movieList ?? [];
      const seen = new Set();
      setMovies(all.filter(m => {
        const mId = String(m.filmid || m.id);
        if (mId === String(excludeId) || seen.has(mId)) return false;
        seen.add(mId); return true;
      }).slice(0, 20));
    }
    load();
  }, [excludeId]);
  return movies;
}

export function useHomeDataCache() {
  const [state, setState] = useState({ banners: _homeCache.banners, categories: _homeCache.categories });
  useEffect(() => {
    if (_homeCache.loaded) return;
    const interval = setInterval(() => {
      if (_homeCache.loaded) { setState({ banners: _homeCache.banners, categories: _homeCache.categories }); clearInterval(interval); }
    }, 300);
    return () => clearInterval(interval);
  }, []);
  return state;
}

export function useHomeData() {
  const [banners,    setBanners]    = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // fetchMovieBanner and fetchMovieList automatically choose the right endpoint:
      //   • Guest (no token) → /tv  endpoint (API key only, works immediately)
      //   • Logged in        → /mobile endpoint (Firebase token, personalised data)
      const [bannerRes, listRes] = await Promise.all([fetchMovieBanner(), fetchMovieList()]);
      const b = (bannerRes?.data ?? []).flatMap(row => row.movies ?? []);
      const c = listRes?.data ?? [];
      setBanners(b); setCategories(c);
      _homeCache = { banners: b, categories: c, loaded: true };
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Connection error');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  return { banners, categories, loading, error, refetch: load };
}

export function useShowDetail(showId) {
  const [show,     setShow]     = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const load = useCallback(async () => {
    if (!showId) return;
    setLoading(true); setError(null);
    try {
      const [detailRes, episodeRes] = await Promise.all([fetchProgramDetail(showId), fetchEpisodeList(showId)]);
      setShow(detailRes?.data ?? detailRes);
      setEpisodes(episodeRes?.data ?? episodeRes ?? []);
    } catch (err) { setError(err?.response?.data?.message || err.message); }
    finally { setLoading(false); }
  }, [showId]);

  useEffect(() => { load(); }, [load]);
  return { show, episodes, loading, error, refetch: load };
}

export function useSearch(query) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  useEffect(() => {
    if (!query || query.trim().length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true); setError(null);
      try { setResults((await fetchSearchList(query.trim()))?.data ?? []); }
      catch (err) { setError(err.message); }
      finally { setLoading(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);
  return { results, loading, error };
}

// ── Watchlist singleton — fetches ONCE, shared across all card instances ──────
// Without this, every MovieCard on screen calls favlist simultaneously (20+ calls)
let _wlCache     = null;        // cached list
let _wlFetching  = false;       // dedupe in-flight requests
let _wlListeners = new Set();   // components waiting for updates

function notifyWlListeners(list) {
  _wlCache = list;
  _wlListeners.forEach(fn => fn(list));
}

async function fetchWlOnce() {
  if (_wlFetching) return;
  if (!getAuthToken()) return;
  _wlFetching = true;
  try {
    const res = await getWatchList();
    notifyWlListeners(res?.data ?? []);
  } catch (_) {}
  finally { _wlFetching = false; }
}

export function useWatchlist() {
  const [list, setList] = useState(_wlCache ?? []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Register as listener for cache updates
    _wlListeners.add(setList);
    // Fetch only if not already cached
    if (_wlCache === null && getAuthToken()) fetchWlOnce();
    else if (_wlCache) setList(_wlCache);
    return () => { _wlListeners.delete(setList); };
  }, []); // eslint-disable-line

  const load = useCallback(async () => {
    if (!getAuthToken()) return;
    setLoading(true);
    await fetchWlOnce();
    setLoading(false);
  }, []);

  const toggleWatchlist = async (filmId, isInList) => {
    try {
      if (isInList) {
        const updated = (list).filter(m => m.filmid !== filmId && String(m.id) !== String(filmId));
        notifyWlListeners(updated);
        await removeFromWatchlist(filmId);
      } else {
        const alreadyIn = (list).some(m => m.filmid === filmId || String(m.id) === String(filmId));
        if (!alreadyIn) notifyWlListeners([...(list), { filmid: filmId, favitem: 1 }]);
        await addToWatchlist(filmId);
        _wlCache = null; // force refresh
        fetchWlOnce();
      }
    } catch (_) {
      _wlCache = null;
      fetchWlOnce();
    }
  };

  return { list, loading, toggleWatchlist, refetch: load };
}

export function useSubscriptionPlans() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    fetchSubscriptionPlans().then(setData).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, []);
  return { data, loading, error };
}

export function useGeneralMessage() {
  const [data, setData] = useState(null);
  useEffect(() => { fetchGeneralMessage().then(setData).catch(() => {}); }, []);
  return { data };
}

export function useWatchProgress() {
  // reportEvent — sends play/pause/stop/end activity to backend
  // event must be one of: play_video | pause_video | stop_video | end_video
  const reportEvent = useCallback(async (event, meta) => {
    try { await completionEvent(event, meta); }
    catch (err) { console.warn('Watch progress event failed:', err.message); }
  }, []);

  // getResumePosition — fetches last watched position in seconds from backend
  // Returns 0 if no saved position or user is not logged in
  const getResumePosition = useCallback(async (filmId, episodeId = null) => {
    try { return await fetchResumePosition(filmId, episodeId); }
    catch (err) { console.warn('Resume position fetch failed:', err.message); return 0; }
  }, []);

  return { reportEvent, getResumePosition };
}

// Continue Watching — data is embedded in sflist (sflistwh view), no separate endpoint
// The activity/history/mobile endpoint does not exist on this backend
export function useContinueWatching() {
  // Continue watching data comes from the home data categories
  // Look for a genre row with continuewatch='Y' flag in useHomeData results
  return { items: [], loading: false, invalidate: () => {} };
}
